import { useContext } from 'react';

import { Typography } from '@mui/material';

import { LayerContext } from 'state/LayerContext';
import { useGetLayerQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlPopup } from 'features/common/StlPopup/StlPopup';
import { getRouteForSparseSegments } from 'features/map/utils/route_utils';

import {
  selectScenarioCapacity,
  selectScenarioDaysOfWeekInfo,
  selectScenarioEnd,
  selectScenarioName,
  selectScenarioStart,
  setScenarioIsSaving,
  useEditScenarioMutation,
} from '../../../state/detourSlices';
import {
  selectCurrentScenarioId,
  selectProject,
  selectRouteSelection,
  selectSegmentId,
  selectTypicalMonth,
} from '../../../state/workflowSlice';
import { useScenarioUI } from '../hooks/useScenarioUI';
import { IScenarioPopup } from './utils';

import styles from './detour_popup.module.css';

const TXT = `You've made changes to this scenario. Do you want to save them and run the updated version?`;

export default function ScenarioEditPopup({ open, setOpen }: IScenarioPopup) {
  const dispatch = useAppDispatch();

  const { layer } = useContext(LayerContext);

  const scenarioName = useAppSelector(selectScenarioName);
  const { daysOfWeek } = useAppSelector(selectScenarioDaysOfWeekInfo);
  const closureStart = useAppSelector(selectScenarioStart);
  const closureEnd = useAppSelector(selectScenarioEnd);
  const selectedSegmentId = useAppSelector(selectSegmentId);
  const routeSelection = useAppSelector(selectRouteSelection);
  const project_slug = useAppSelector(selectProject);
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const capacity = useAppSelector(selectScenarioCapacity);
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);

  const [editScenario] = useEditScenarioMutation();
  const { currentData: layerData } = useGetLayerQuery(layer, { skip: !layer });
  const { setScenariosMode } = useScenarioUI();

  const handleApply = async () => {
    let routeSegmentIds = [selectedSegmentId];
    if (routeSelection?.length > 0 && layerData) {
      routeSegmentIds = getRouteForSparseSegments(
        layerData.routing,
        routeSelection,
      );
    }
    try {
      dispatch(setScenarioIsSaving(true));
      await editScenario({
        scenario_settings: {
          scenario_name: scenarioName,
          scenario_id: currentScenarioId,
          days_of_week: daysOfWeek,
          closure_start: closureStart,
          closure_end: closureEnd,
          segments: routeSegmentIds,
          new_capacities: [capacity],
          project_slug,
          year_month: typicalMonth,
        },
        project_slug,
      })
        .unwrap()
        .then((res) => {
          setScenariosMode(res.scenario);
          dispatch(setScenarioIsSaving(false));
        });
    } catch (e) {
      console.error(e);
      dispatch(setScenarioIsSaving(false));
    }
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <StlPopup
      className={styles.detour_popup_wide}
      btnTxt="Proceed"
      title={
        <span>
          <FontAwesomeIcon
            type="sharp-solid"
            name="triangle-exclamation"
            gold
            sx={{ marginRight: '2px' }}
          />
          Confirm Changes
        </span>
      }
      isOpen={open}
      onClose={handleClose}
      onApply={handleApply}
    >
      <Typography className={styles.inner_txt} lineHeight="24px">
        {TXT}
      </Typography>
    </StlPopup>
  );
}
